import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Api from "../api/Api";
import { toast } from "react-toastify";

// ─── Estilos injetados via <style> ───────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  /* ── Overlay — apenas posicionamento, sem bloquear cliques fora ── */
  .um-overlay {
    position: fixed;
    inset: 0;
    z-index: 1001;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  /* ── Backdrop sutil para foco visual (não intercepta clique) ── */
  .um-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(5, 10, 20, 0.55);
    backdrop-filter: blur(3px);
    z-index: 1000;
    animation: um-fade-in 0.2s ease;
    pointer-events: none;
  }
  @keyframes um-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* ── Panel ── */
  .um-panel {
    pointer-events: all;
    width: 420px;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 60px);
    background: linear-gradient(160deg, #0e1e3a 0%, #091428 100%);
    border: 1px solid rgba(0, 120, 255, 0.2);
    border-radius: 18px;
    box-shadow:
      0 24px 60px rgba(0,0,0,0.6),
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 1px 0 rgba(255,255,255,0.08) inset;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: um-panel-in 0.28s cubic-bezier(0.34,1.2,0.64,1);
    font-family: 'Inter', sans-serif;
  }
  @keyframes um-panel-in {
    from { opacity: 0; transform: translateY(-16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Header ── */
  .um-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 22px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    flex-shrink: 0;
  }
  .um-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .um-header-icon {
    width: 34px;
    height: 34px;
    background: linear-gradient(135deg, #0057d9, #0095ff);
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0,87,217,0.4);
  }
  .um-header-icon svg { width: 16px; height: 16px; color: #fff; }
  .um-title {
    font-size: 15px;
    font-weight: 600;
    color: #e8edf8;
    letter-spacing: -0.2px;
  }
  .um-subtitle {
    font-size: 11px;
    color: rgba(255,255,255,0.38);
    margin-top: 1px;
  }
  .um-close-btn {
    width: 30px;
    height: 30px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.45);
    transition: background 0.2s, color 0.2s;
    outline: none;
  }
  .um-close-btn:hover {
    background: rgba(255,255,255,0.12);
    color: #fff;
  }
  .um-close-btn svg { width: 14px; height: 14px; }

  /* ── Body ── */
  .um-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 22px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.1) transparent;
  }
  .um-body::-webkit-scrollbar { width: 4px; }
  .um-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }

  /* ── Loading ── */
  .um-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
  }
  .um-spinner {
    width: 28px;
    height: 28px;
    border: 2px solid rgba(255,255,255,0.08);
    border-top-color: #0078ff;
    border-radius: 50%;
    animation: um-spin 0.7s linear infinite;
  }
  @keyframes um-spin { to { transform: rotate(360deg); } }

  /* ── Avatar Section ── */
  .um-avatar-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 28px;
  }
  .um-avatar-wrap {
    position: relative;
    width: 92px;
    height: 92px;
  }
  .um-avatar-img {
    width: 92px;
    height: 92px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid rgba(0, 140, 255, 0.35);
    box-shadow: 0 8px 24px rgba(0,87,217,0.35);
    background: rgba(255,255,255,0.06);
  }
  .um-avatar-fallback {
    width: 92px;
    height: 92px;
    border-radius: 50%;
    border: 3px solid rgba(0, 140, 255, 0.35);
    background: linear-gradient(135deg, rgba(0,87,217,0.3), rgba(0,140,255,0.15));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5aadff;
    font-size: 32px;
    font-weight: 600;
  }
  .um-avatar-edit-btn {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0057d9, #0095ff);
    border: 2px solid #0e1e3a;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    transition: transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 10px rgba(0,87,217,0.5);
  }
  .um-avatar-edit-btn:hover {
    transform: scale(1.08);
  }
  .um-avatar-edit-btn svg { width: 13px; height: 13px; }

  .um-name-display {
    font-size: 17px;
    font-weight: 600;
    color: #e8edf8;
    text-align: center;
  }
  .um-name-display.muted {
    color: rgba(255,255,255,0.35);
    font-style: italic;
    font-weight: 500;
  }
  .um-funcao-display {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
    text-align: center;
    margin-top: -8px;
  }
  .um-funcao-display.muted {
    color: rgba(255,255,255,0.22);
    font-style: italic;
  }

  /* ── Fields (view mode) ── */
  .um-field-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .um-field-view {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
  }
  .um-field-label {
    font-size: 10.5px;
    font-weight: 600;
    color: rgba(90,173,255,0.85);
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .um-field-label svg { width: 11px; height: 11px; }
  .um-field-value {
    font-size: 13.5px;
    color: #e8edf8;
    word-break: break-word;
  }
  .um-field-value.muted {
    color: rgba(255,255,255,0.3);
    font-style: italic;
  }

  /* ── Edit mode inputs ── */
  .um-field-edit {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .um-input-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255,255,255,0.5);
    display: flex;
    align-items: center;
    gap: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .um-input-label svg { width: 11px; height: 11px; color: #5aadff; }
  .um-input,
  .um-textarea {
    width: 100%;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    color: #e8edf8;
    font-size: 13.5px;
    padding: 10px 13px;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    box-sizing: border-box;
    font-family: 'Inter', sans-serif;
  }
  .um-input {
    height: 40px;
  }
  .um-textarea {
    min-height: 70px;
    resize: vertical;
    line-height: 1.5;
  }
  .um-input::placeholder,
  .um-textarea::placeholder {
    color: rgba(255,255,255,0.25);
  }
  .um-char-count {
    font-size: 10.5px;
    color: rgba(255,255,255,0.3);
    text-align: right;
    margin-top: -2px;
  }
  .um-input:focus,
  .um-textarea:focus {
    border-color: rgba(0, 140, 255, 0.5);
    background: rgba(255,255,255,0.07);
    box-shadow: 0 0 0 3px rgba(0,120,255,0.12);
  }
  .um-input:disabled,
  .um-textarea:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .um-field-readonly {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 13px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 10px;
    font-size: 12.5px;
    color: rgba(255,255,255,0.35);
  }
  .um-field-readonly svg { width: 12px; height: 12px; flex-shrink: 0; }

  /* ── Footer ── */
  .um-footer {
    padding: 16px 22px;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }
  .um-btn {
    flex: 1;
    height: 42px;
    border-radius: 11px;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s, transform 0.1s, background 0.2s;
    font-family: 'Inter', sans-serif;
    border: none;
  }
  .um-btn:active:not(:disabled) { transform: scale(0.98); }
  .um-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  .um-btn-edit {
    background: linear-gradient(135deg, #0057d9, #0095ff);
    color: #fff;
    box-shadow: 0 4px 14px rgba(0,87,217,0.35);
  }
  .um-btn-edit:hover:not(:disabled) { opacity: 0.92; }

  .um-btn-cancel {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.6);
    flex: 1;
  }
  .um-btn-cancel:hover:not(:disabled) {
    background: rgba(255,255,255,0.1);
    color: #fff;
  }

  .um-btn-save {
    background: linear-gradient(135deg, #0057d9, #0095ff);
    color: #fff;
    flex: 2;
    box-shadow: 0 4px 14px rgba(0,87,217,0.35);
  }
  .um-btn-save:hover:not(:disabled) { opacity: 0.92; }

  .um-btn svg { width: 14px; height: 14px; }

  .um-spinner-sm {
    width: 15px;
    height: 15px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top: 2px solid #fff;
    border-radius: 50%;
    animation: um-spin 0.65s linear infinite;
  }

  @media (max-width: 480px) {
    .um-panel { width: 100%; border-radius: 14px; }
  }
`;

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18"/>
    <path d="M5 21V7l8-4v18"/>
    <path d="M19 21V11l-6-4"/>
  </svg>
);
const FileTextIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitial = (nome) => {
  if (!nome || nome === "0") return "?";
  return nome.trim().charAt(0).toUpperCase();
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UserProfileModal = ({ open, onClose }) => {
  const userStored = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dados, setDados]       = useState(null);

  // Campos editáveis
  // ATENÇÃO: o endpoint de edição (Api.AlterUser) usa "descricao" para se
  // referir à descrição profissional do usuário (campo "descricao_profissional"
  // retornado pelo GET). O "descricao" do GET é a direção/departamento e
  // NÃO é editável por este endpoint.
  const [nome, setNome]                       = useState("");
  const [descricaoProfissional, setDescProf]  = useState("");
  const [previewImg, setPreviewImg]           = useState(null);
  const [fileImg, setFileImg]                 = useState(null);

  const panelRef    = useRef(null);
  const fileInputRef = useRef(null);

  const loadDados = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Api.ListuserDados({ id: userStored.id });
      setDados(data);
      setNome(data?.nome && data.nome !== "0" ? data.nome : "");
      setDescProf(data?.descricao_profissional && data.descricao_profissional !== "0" ? data.descricao_profissional : "");
    } catch (err) {
      console.error("Erro ao carregar dados do usuário:", err);
      toast.error("Não foi possível carregar os dados do usuário.");
    } finally {
      setLoading(false);
    }
  }, [userStored.id]);

  // Carrega dados quando o modal abre
  useEffect(() => {
    if (open) {
      loadDados();
      setEditMode(false);
      setPreviewImg(null);
      setFileImg(null);
    }
  }, [open, loadDados]);

  // Fechar com Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Fechar ao clicar fora do painel
  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, onClose]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 10MB.");
      return;
    }

    setFileImg(file);
    setPreviewImg(URL.createObjectURL(file));
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setNome(dados?.nome && dados.nome !== "0" ? dados.nome : "");
    setDescProf(dados?.descricao_profissional && dados.descricao_profissional !== "0" ? dados.descricao_profissional : "");
    setPreviewImg(null);
    setFileImg(null);
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.warning("O nome é obrigatório.");
      return;
    }
    if (!descricaoProfissional.trim()) {
      toast.warning("A descrição profissional é obrigatória.");
      return;
    }
    if (descricaoProfissional.length > 500) {
      toast.warning("A descrição profissional deve ter no máximo 500 caracteres.");
      return;
    }

    const fd = new FormData();
    fd.append("id", userStored.id);
    fd.append("nome", nome);
    fd.append("descricao", descricaoProfissional);
    if (fileImg) fd.append("file", fileImg);

    // ── DEBUG: confirmar o que está realmente a ser enviado ──
    // Remove este bloco depois de confirmares que está tudo certo.
    console.log("=== FormData enviado para Api.AlterUser ===");
    for (const [key, val] of fd.entries()) {
      console.log(key, "=>", val);
    }
    console.log("fileImg state:", fileImg);

    setSaving(true);
    try {
      await Api.AlterUser(fd);
      toast.success("Dados atualizados com sucesso!");
      await loadDados();
      setEditMode(false);
      setPreviewImg(null);
      setFileImg(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao atualizar os dados.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const displayImg = previewImg || dados?.imagem;
  const displayNome = nome || (dados?.nome && dados.nome !== "0" ? dados.nome : null);

  return createPortal(
    <>
      <style>{styles}</style>
      <div className="um-backdrop" />
      <div className="um-overlay">
        <div ref={panelRef} className="um-panel" role="dialog" aria-label="Perfil do Usuário">

          {/* Header */}
          <div className="um-header">
            <div className="um-header-left">
              <div className="um-header-icon"><UserIcon /></div>
              <div>
                <div className="um-title">Meu Perfil</div>
                <div className="um-subtitle">
                  {editMode ? "Editando informações" : "Dados da sua conta"}
                </div>
              </div>
            </div>
            <button className="um-close-btn" onClick={onClose} aria-label="Fechar">
              <CloseIcon />
            </button>
          </div>

          {/* Body */}
          <div className="um-body">
            {loading ? (
              <div className="um-loading"><div className="um-spinner" /></div>
            ) : (
              <>
                {/* Avatar */}
                <div className="um-avatar-section">
                  <div className="um-avatar-wrap">
                    {displayImg ? (
                      <img src={displayImg} alt="Avatar" className="um-avatar-img" />
                    ) : (
                      <div className="um-avatar-fallback">{getInitial(dados?.nome)}</div>
                    )}
                    {editMode && (
                      <button
                        type="button"
                        className="um-avatar-edit-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Alterar foto"
                      >
                        <CameraIcon />
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </div>
                  {!editMode && (
                    <>
                      <div className={`um-name-display ${!displayNome ? "muted" : ""}`}>
                        {displayNome || "Nome não definido"}
                      </div>
                      <div className={`um-funcao-display ${!dados?.funcao ? "muted" : ""}`}>
                        {dados?.funcao || "Função não definida"}
                      </div>
                    </>
                  )}
                </div>

                {!editMode ? (
                  /* ── Modo visualização ── */
                  <div className="um-field-group">
                    <div className="um-field-view">
                      <span className="um-field-label"><MailIcon /> Email</span>
                      <span className={`um-field-value ${!dados?.email ? "muted" : ""}`}>
                        {dados?.email || "Não definido"}
                      </span>
                    </div>

                    <div className="um-field-view">
                      <span className="um-field-label"><BriefcaseIcon /> Função</span>
                      <span className={`um-field-value ${!dados?.funcao ? "muted" : ""}`}>
                        {dados?.funcao || "Não definida"}
                      </span>
                    </div>

                    <div className="um-field-view">
                      <span className="um-field-label"><BuildingIcon /> Direção / Departamento</span>
                      <span className={`um-field-value ${!dados?.descricao ? "muted" : ""}`}>
                        {dados?.descricao || "Não definido"}
                      </span>
                    </div>

                    <div className="um-field-view">
                      <span className="um-field-label"><FileTextIcon /> Descrição Profissional</span>
                      <span className={`um-field-value ${(!dados?.descricao_profissional || dados.descricao_profissional === "0") ? "muted" : ""}`}>
                        {dados?.descricao_profissional && dados.descricao_profissional !== "0"
                          ? dados.descricao_profissional
                          : "Não definida"}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* ── Modo edição ── */
                  <div className="um-field-group">
                    <div className="um-field-edit">
                      <span className="um-input-label"><UserIcon /> Nome</span>
                      <input
                        type="text"
                        className="um-input"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        placeholder="Seu nome completo"
                        disabled={saving}
                      />
                    </div>

                    <div className="um-field-edit">
                      <span className="um-input-label"><FileTextIcon /> Descrição Profissional</span>
                      <textarea
                        className="um-textarea"
                        value={descricaoProfissional}
                        onChange={(e) => setDescProf(e.target.value.slice(0, 500))}
                        placeholder="Fale um pouco sobre a sua atuação profissional"
                        maxLength={500}
                        disabled={saving}
                      />
                      <span className="um-char-count">{descricaoProfissional.length}/500</span>
                    </div>

                    <div className="um-field-readonly">
                      <LockIcon /> Email e direção não podem ser alterados aqui
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && (
            <div className="um-footer">
              {!editMode ? (
                <button className="um-btn um-btn-edit" onClick={() => setEditMode(true)}>
                  <EditIcon /> Editar Perfil
                </button>
              ) : (
                <>
                  <button className="um-btn um-btn-cancel" onClick={handleCancelEdit} disabled={saving}>
                    Cancelar
                  </button>
                  <button className="um-btn um-btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? <div className="um-spinner-sm" /> : <><SaveIcon /> Salvar</>}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default UserProfileModal;